import {
  Box,
  Collapse,
  ListItem, ListItemButton, ListItemIcon, ListItemProps,
  ListItemText, MenuItem, MenuList, Paper, Popper, styled, Tooltip, useTheme
} from "@mui/material";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import { CabComponentProps } from "../../cabStyled";
import colors from "../../../../colors";
import { PassedComponent } from "../../../../utils/types";
import { CabIcon } from "../../CabIcon";
import { isMobile } from "../../../../utils/screenSizeUtils";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CabToggleChip } from "../../CabToggleChip";
import { IconType } from "react-icons";
import { IoChevronDownOutline, IoLockClosedOutline, IoReorderTwo } from "react-icons/io5";
import { useLocation } from "react-router";
import { CabTooltip } from "@CabComponents/CabTooltip";

export interface CabNavItemProps extends CabComponentProps<ListItemProps> {
  active: boolean;
  open: boolean;
  key: string;
  Icon?: IconType;
  iconSize?: number;
  iconStrokeWidth?: number;
  IconRight?: IconType;
  secondaryIcon?: IconType;
  secondaryIconTooltip?: string;
  iconOverride?: PassedComponent<object>;
  text: string;
  locked?: boolean;
  hidden?: boolean;
  onClick?: () => void;
  onClickLocked?: () => void;
  onClickMobile?: () => void;
  sx?: ListItemProps['sx'];
  className?: ListItemProps['className'];
  childItems?: CabNavItemProps[]
  handleNavbarDrawer?: (value: boolean) => void
  sortId?: number;
  sortable?: boolean;
  newIcon?: boolean;
  elementId?: ListItemProps['id'];
  path?: string;
  uncollapseAll?: boolean;
  setUncollapseAll?: (value: boolean) => void
  collapsedPopover?: boolean;
  alwaysExpanded?: boolean;
}

type CabNavBarButtonType  = Omit<CabNavItemProps, "key" | "sx" | "className"> & {
  openCollapse: boolean;
};

// Does this need to have forwardRef? Maybe it used to, or maybe MUI requires it for some reason.
export const CabNavBarButton = React.forwardRef<HTMLDivElement | null, CabNavBarButtonType>((
  { 
    locked, hidden, text, Icon, iconSize, iconStrokeWidth, IconRight, secondaryIcon, secondaryIconTooltip,
    active, open, iconOverride, childItems, alwaysExpanded,
    onClickLocked, onClickMobile, onClick, openCollapse, sortable, newIcon, sortId, elementId
  },
  ref
): ReactElement => {
  
  const handleListItemClick = () => {
    if (locked && onClickLocked) {
      onClickLocked();
    } else if (isMobile() && onClickMobile) {
      onClickMobile();
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <>
      {!hidden && (
        <>
          <ListItemButtonStyled onClick={handleListItemClick} open={open} sortable={sortable} ref={ref} 
            id={elementId}>
            <ListItemIconStyled open={open} sortable={sortable}>
              {iconOverride ?
                <Box
                  sx={{
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center'
                  }}>
                  {sortable && open && (
                    <CabIcon size="small" alt="Close" Icon={IoReorderTwo} />
                  )}
                  {iconOverride.component(iconOverride.props)}
                </Box>
                :
                Icon ? <IconStyled
                  Icon={Icon}
                  alt={text}
                  active={active}
                  sx={{
                    fontSize: iconSize,
                    ...(iconStrokeWidth ? {
                      '& path': {
                        paintOrder: 'stroke fill',
                        stroke: 'currentColor',
                        strokeWidth: iconStrokeWidth,
                      },
                    } : {}),
                  }}
                /> : <></>
              }
            </ListItemIconStyled>

            <ListItemTextStyled open={open} hidden={!open} slotProps={{
              primary: {
                sx: {
                  ...(active ? { fontWeight: 700 } : {}),
                  overflow: "visible",
                  textOverflow: "clip",
                },
                // color: colors.black900,
                noWrap: true
              }
            }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  fontSize: 14,
                  color: active ? colors.lavendar800 : colors.calmGrey800,
                  width: '100%'
                }}>
                {text}
                {secondaryIcon && (
                  <CabTooltip title={secondaryIconTooltip || ''} placement="top" arrow wrapWithSpan>
                    <CabIcon
                      size={14}
                      Icon={secondaryIcon}
                      sx={{ 
                        marginLeft: 0.5, 
                        color: active ? colors.lavendar800 : colors.black500,
                        fontWeight: active ? 700 : 400
                      }}
                    />
                  </CabTooltip>
                )}
                {newIcon && (
                  <CabToggleChip
                    label="new"
                    themeColor="primary"
                    selected
                    sx={{
                      height: 18,
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                      px: 0.75,
                      ml: 1,
                      '& .MuiChip-label': {
                        padding: 0,
                      },
                    }}
                  />
                )}
                {(childItems !== undefined && childItems.length > 0 && !alwaysExpanded) &&
                  <CabIcon
                    sx={{
                      marginLeft: 'auto',
                      fontSize: 14,
                      transform: `${openCollapse ? "rotateX(180deg)" : "rotateX(0deg)"}`
                    }}
                    Icon={IoChevronDownOutline}
                  />
                }
              </Box>
            </ListItemTextStyled>
            {(locked && open)
              ? <ListItemIconStyled open={false}>
                <IconStyled Icon={IoLockClosedOutline} alt='Locked' active={active} />
              </ListItemIconStyled>
              : (IconRight && open) &&
              <ListItemIconStyled open={false}>
                <IconStyled active={active} Icon={IconRight} alt={text} />
              </ListItemIconStyled>
            }
          </ListItemButtonStyled>
        </>
      )}
    </>
  );
});


export const CabNavItem = (
  { 
    sx, className, locked, hidden, onClickMobile, onClickLocked, onClick, text, Icon, IconRight, 
    secondaryIcon, secondaryIconTooltip, active, open, sortId, childItems, handleNavbarDrawer, 
    iconOverride, iconSize, iconStrokeWidth, sortable, newIcon, elementId, path, uncollapseAll, setUncollapseAll,
    collapsedPopover, alwaysExpanded
  }: CabNavItemProps): ReactElement => {

  const [openCollapse, setOpenCollapse] = useState(Boolean(open && alwaysExpanded));
  const [ignoreCollapse, setIgnoreCollapse] = useState(false);
  const [collapsedPopoverAnchorEl, setCollapsedPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const collapsedPopoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const theme = useTheme();
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    if (!open) {
      setOpenCollapse(false);
    } else {
      if (alwaysExpanded) {
        setOpenCollapse(true);
      }
      setCollapsedPopoverAnchorEl(null);
    }
  }, [alwaysExpanded, open]);

  useEffect(() => () => {
    if (collapsedPopoverCloseTimer.current) {
      clearTimeout(collapsedPopoverCloseTimer.current);
    }
  }, []);

  useEffect(() => {
    if (uncollapseAll && setUncollapseAll && !ignoreCollapse && !alwaysExpanded) {
      setOpenCollapse(false);
      setUncollapseAll(false);
    }
  }, [alwaysExpanded, ignoreCollapse, setUncollapseAll, uncollapseAll]);

  useEffect(() => {
    if (ignoreCollapse && openCollapse ) {
      setIgnoreCollapse(false);
    }
  }, [ignoreCollapse, openCollapse]);

  const {
    setNodeRef,
    attributes,
    listeners,
    transition,
    transform
  } = useSortable({id: sortId || -1});

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const handleOpenCollapse = (openCollapseParam: boolean) => {
    if (setUncollapseAll) {
      setIgnoreCollapse(true);
      setUncollapseAll(true);
    }
    setOpenCollapse(openCollapseParam);
    if (handleNavbarDrawer && openCollapseParam) {
      handleNavbarDrawer(openCollapseParam);
    }
  };

  const getClickHandler = (baseHandler: (() => void) | undefined): (() => void) | undefined => {
    if (baseHandler) {
      if ((childItems || []).length > 0) {
        if (alwaysExpanded) return undefined;
        return () => handleOpenCollapse(!openCollapse);
      } else {
        return baseHandler;
      }
    }
    return undefined;
  };

  const keepCollapsedPopoverOpen = () => {
    if (collapsedPopoverCloseTimer.current) {
      clearTimeout(collapsedPopoverCloseTimer.current);
      collapsedPopoverCloseTimer.current = null;
    }
  };

  const openCollapsedPopover = (anchorEl: HTMLElement) => {
    if (collapsedPopover && !open && childItems?.length) {
      keepCollapsedPopoverOpen();
      setCollapsedPopoverAnchorEl(anchorEl);
    }
  };

  const scheduleCollapsedPopoverClose = () => {
    keepCollapsedPopoverOpen();
    collapsedPopoverCloseTimer.current = setTimeout(() => {
      setCollapsedPopoverAnchorEl(null);
    }, 150);
  };

  const handleCollapsedPopoverItemClick = (childItem: CabNavItemProps) => {
    setCollapsedPopoverAnchorEl(null);
    if (childItem.locked && childItem.onClickLocked) {
      childItem.onClickLocked();
    } else {
      childItem.onClick?.();
    }
  };

  const collapsedPopoverOpen = Boolean(
    collapsedPopover && !open && childItems?.length && collapsedPopoverAnchorEl
  );

  return (!sortable ? (<>
    {!hidden && (
      <>
        <ListItemStyled
          disablePadding
          id={elementId}
          active={active}
          sx={sx}
          className={className}
          onPointerEnter={(event) => openCollapsedPopover(event.currentTarget)}
          onPointerLeave={collapsedPopover && !open ? scheduleCollapsedPopoverClose : undefined}
          onFocus={(event) => openCollapsedPopover(event.currentTarget)}
          onBlur={collapsedPopover && !open ? scheduleCollapsedPopoverClose : undefined}
        >
          <Tooltip
            placement="right" title={open || (collapsedPopover && childItems?.length) ? "" : text}
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: theme.typography.body2,
                  color: colors.white900
                }
              }
            }}
          >
            <Box id={elementId} sx={{
              width: '100%'
            }}>
              <CabNavBarButton
                locked={locked}
                hidden={hidden}
                onClickMobile={getClickHandler(onClickMobile)}
                onClickLocked={getClickHandler(onClickLocked)}
                onClick={getClickHandler(onClick)}
                text={text}
                Icon={Icon}
                iconSize={iconSize}
                iconStrokeWidth={iconStrokeWidth}
                IconRight={IconRight}
                secondaryIcon={secondaryIcon}
                secondaryIconTooltip={secondaryIconTooltip}
                active={active}
                open={open}
                iconOverride={iconOverride}
                childItems={childItems}
                alwaysExpanded={alwaysExpanded}
                openCollapse={openCollapse}
                newIcon={newIcon}
                path={path}
              />
            </Box>
          </Tooltip>
          {(locked && !open && !hidden) &&
        <CollapsedLockIcon active={active} alt='Locked' Icon={IoLockClosedOutline} />}
        </ListItemStyled>
        <Popper
          open={collapsedPopoverOpen}
          anchorEl={collapsedPopoverAnchorEl}
          placement='right-start'
          sx={{ zIndex: theme.zIndex.modal }}
        >
          <Paper
            elevation={4}
            onMouseEnter={keepCollapsedPopoverOpen}
            onMouseLeave={scheduleCollapsedPopoverClose}
            sx={{
              border: `1px solid ${colors.black200}`,
              borderRadius: 1.5,
              marginLeft: 1,
              minWidth: 190,
              paddingY: 0.75,
            }}
          >
            <MenuList aria-label={`${text} submenu`} disablePadding>
              <Box
                component='li'
                role='presentation'
                sx={{
                  color: colors.calmGrey400,
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  listStyle: 'none',
                  paddingBottom: 0.5,
                  paddingLeft: 2,
                  paddingRight: 2,
                  paddingTop: 1,
                }}
              >
                {text}
              </Box>
              {childItems?.filter(childItem => !childItem.hidden).map(childItem => (
                <MenuItem
                  key={childItem.key}
                  onClick={() => handleCollapsedPopoverItemClick(childItem)}
                  sx={{
                    color: childItem.path === pathname ? colors.lavendar800 : colors.calmGrey800,
                    fontSize: 14,
                    fontWeight: childItem.path === pathname ? 700 : 400,
                    minHeight: 38,
                    paddingX: 2,
                    '&:hover': {
                      backgroundColor: colors.black100,
                    },
                  }}
                >
                  <Box component='span' sx={{ flex: 1 }}>{childItem.text}</Box>
                  {childItem.locked && <CabIcon Icon={IoLockClosedOutline} alt='Locked' size={14} />}
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </Popper>
      </>
    )}
    {childItems && !hidden &&
    <Collapse in={openCollapse}>
      {childItems.map((childItem => {
        return <CabNavItem
          {...childItem}
          key={childItem.key}
          sx={{ marginLeft: childItem.Icon ? 1 : undefined }}
          active={childItem.path === pathname}
        />;
      }))}
    </Collapse>
    }
  </>) : (<div ref={setNodeRef} style={style} {...attributes}{...listeners}>
    {!hidden && (
      <ListItemStyled disablePadding active={active} sx={sx} className={className}>
        <CabNavBarButton
          locked={locked}
          hidden={hidden}
          onClickMobile={getClickHandler(onClickMobile)}
          onClickLocked={getClickHandler(onClickLocked)}
          onClick={getClickHandler(onClick)}
          text={text}
          Icon={Icon}
          iconSize={iconSize}
          iconStrokeWidth={iconStrokeWidth}
          IconRight={IconRight}
          active={active}
          open={open}
          iconOverride={iconOverride}
          childItems={childItems}
          alwaysExpanded={alwaysExpanded}
          openCollapse={openCollapse}
          sortable={sortable}
          path={path}
        />
        {(locked && !open && !hidden) && 
        <CollapsedLockIcon active={active} alt='Locked' Icon={IoLockClosedOutline} />}
      </ListItemStyled>
    )}
    {childItems && !hidden &&
    <Collapse in={openCollapse}>
      {childItems.map((childItem => {
        return <CabNavItem {...childItem} key={childItem.key} />;
      }))}
    </Collapse>
    }
  </div>));
};

const ListItemTextStyled = styled(
  ListItemText, { shouldForwardProp: (prop) => prop !== 'open', label: "ListItemTextStyled" }
)<{open: boolean}>(({open}) => ({
  opacity: open ? 1 : 0,
  marginTop: 0,
  marginBottom: 0,
  color: colors.calmGrey500,
}));

const ListItemIconStyled = styled(ListItemIcon, {
  shouldForwardProp: (prop: string) => 
    !['open', 'sortable'].includes(prop),
  label: "ListItemIconStyled"
})<{open: boolean, sortable?: boolean}>(({theme, open, sortable}) => ({
  ...(open ? {} : {
    justifyContent: "center",
  }),
  ...(!sortable ? {} : {
    marginRight: 8,
  }),
  minWidth: 28,
  marginTop: 0,
  marginBottom: 0
}));

const IconStyled = styled(CabIcon, {
  shouldForwardProp: prop => prop !== 'active',
  label: "IconStyled"
})<{ Icon: IconType; active: boolean }>(({ theme, active }) => ({
  color: active ? colors.lavendar800 : colors.calmGrey500,
  fontSize: 18
}));


const CollapsedLockIcon = styled(CabIcon, {
  shouldForwardProp: prop => prop !== 'active',
  label: "CollapsedLockIcon"
})<{ Icon: IconType; active: boolean }>(({ theme, active }) => ({
  ...(active ? {
    backgroundColor: colors.black900,
  } : {
    color: colors.black900,
  }),
  fontSize: 12,
  position: "absolute",
  right: 0,
  top: 0,
  padding: 2,
  borderRadius: 9
}));

const ListItemButtonStyled = styled(
  ListItemButton,
  {
    shouldForwardProp: (prop: string) => 
      !['open', 'sortable'].includes(prop),
    label: "ListItemButtonStyled"
  }
)<{open: boolean, sortable?: boolean}>(({open, sortable}) => ({
  ...(open ? {} : {
    paddingLeft: 0,
    paddingRight: 0,
    justifyContent: 'center'
  }),
  ...(!sortable ? {} : {
    paddingLeft: 2,
  }),
  minHeight: 32,
  paddingTop: 2,
  paddingBottom: 2,
}));

const ListItemStyled = styled(ListItem, {
  shouldForwardProp: prop => prop !== 'active',
  label: "ListItemStyled"
})<CabComponentProps<ListItemProps> & { active: boolean }>(({ active }) => ({
  ...(active ? {
    color: colors.lavendar800,
    fontWeight: 700
  } : {
    color: colors.calmGrey500,
  }),
  borderRadius: 8,
  width: 'calc(100% -  16px)',
  marginBottom: 1,
  marginLeft: 8,
  marginRight: 4,
  marginTop: 1,
  position: "relative",
}));
